import React, { useState, useEffect } from 'react';
import { Star, Check, X, Trash2, Award, Pencil, Plus, Search } from 'lucide-react';
import { api } from '../../api/client';
import { showToast } from '../../components/common/Toast';
import Modal from '../../components/common/Modal';

export default function AdminReviewsPage() {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');

  // Edit Modal State
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingReview, setEditingReview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [editFormData, setEditFormData] = useState({
    clientName: '',
    clientTitle: '',
    rating: 5,
    commentUz: '',
    commentRu: '',
    commentEn: '',
    status: 'APPROVED',
    isFeatured: false,
  });

  // Create Modal State
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [savingNew, setSavingNew] = useState(false);
  const [newFormData, setNewFormData] = useState({
    clientName: '',
    clientTitle: '',
    rating: 5,
    commentUz: '',
    commentRu: '',
    commentEn: '',
    status: 'APPROVED',
    isFeatured: true,
  });

  const loadReviews = () => {
    setLoading(true);
    api.get('/reviews?all=true')
      .then((res) => {
        if (res?.success) setReviews(res.reviews || []);
      })
      .catch((err) => showToast(err.message, 'error'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadReviews();
  }, []);

  const handleUpdateStatus = async (id, status) => {
    try {
      const res = await api.put(`/reviews/${id}/status`, { status });
      if (res?.success) {
        showToast(`Sharh holati yangilandi: ${status}`, 'success');
        loadReviews();
      }
    } catch (err) {
      showToast(err.message, 'error');
    }
  };

  const handleToggleFeature = async (id, currentVal) => {
    try {
      const res = await api.put(`/reviews/${id}/status`, { isFeatured: !currentVal });
      if (res?.success) {
        showToast('Bosh sahifada ko‘rsatish holati o‘zgartirildi', 'success');
        loadReviews();
      }
    } catch (err) {
      showToast(err.message, 'error');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Haqiqatan ham ushbu sharhni o‘chirmoqchimisiz?')) return;
    try {
      const res = await api.delete(`/reviews/${id}`);
      if (res?.success) {
        showToast('Sharh o‘chirildi', 'success');
        loadReviews();
      }
    } catch (err) {
      showToast(err.message, 'error');
    }
  };

  const handleOpenEdit = (review) => {
    setEditingReview(review);
    setEditFormData({
      clientName: review.clientName || '',
      clientTitle: review.clientTitle || '',
      rating: review.rating || 5,
      commentUz: review.commentUz || '',
      commentRu: review.commentRu || '',
      commentEn: review.commentEn || '',
      status: review.status || 'APPROVED',
      isFeatured: !!review.isFeatured,
    });
    setIsEditModalOpen(true);
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    if (!editingReview) return;
    setSaving(true);
    try {
      const res = await api.put(`/reviews/${editingReview.id}`, editFormData);
      if (res?.success) {
        showToast('Sharh muvaffaqiyatli tahrirlandi!', 'success');
        setIsEditModalOpen(false);
        setEditingReview(null);
        loadReviews();
      }
    } catch (err) {
      showToast(err.message || 'Tahrirlashda xatolik', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleOpenCreate = () => {
    setNewFormData({
      clientName: '',
      clientTitle: '',
      rating: 5,
      commentUz: '',
      commentRu: '',
      commentEn: '',
      status: 'APPROVED',
      isFeatured: true,
    });
    setIsCreateModalOpen(true);
  };

  const handleSaveNew = async (e) => {
    e.preventDefault();
    if (!newFormData.clientName || !newFormData.commentUz) {
      showToast('Mijoz ismi va fikr matni majburiy', 'error');
      return;
    }
    setSavingNew(true);
    try {
      const res = await api.post('/reviews', newFormData);
      if (res?.success) {
        showToast('Yangi fikr muvaffaqiyatli qo‘shildi!', 'success');
        setIsCreateModalOpen(false);
        loadReviews();
      }
    } catch (err) {
      showToast(err.message || 'Xatolik yuz berdi', 'error');
    } finally {
      setSavingNew(false);
    }
  };

  const filteredReviews = reviews.filter((r) => {
    if (statusFilter !== 'ALL' && r.status !== statusFilter) return false;
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return (
      r.clientName?.toLowerCase().includes(q) ||
      r.clientTitle?.toLowerCase().includes(q) ||
      r.commentUz?.toLowerCase().includes(q) ||
      r.commentRu?.toLowerCase().includes(q) ||
      r.commentEn?.toLowerCase().includes(q) ||
      r.lawyer?.fullName?.toLowerCase().includes(q)
    );
  });

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="font-serif text-base sm:text-lg font-bold text-slate-900">
            Mijozlar Fikrlari va Sharhlar ({filteredReviews.length})
          </h2>
          <p className="text-xs text-slate-500">
            Sharhlarni tasdiqlash, tahrirlash, yangi qo‘shish va bosh sahifada ko‘rsatish
          </p>
        </div>

        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2.5 sm:gap-3">
          <div className="relative flex-1 sm:w-60">
            <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Ism, matn yoki advokat izlash..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-8 py-1.5 text-xs bg-white border border-slate-300 rounded focus:border-[#c5a880] focus:outline-none"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery('')}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 text-xs font-bold"
              >
                ✕
              </button>
            )}
          </div>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-2.5 py-1.5 text-xs bg-white border border-slate-300 rounded text-slate-700 focus:outline-none"
          >
            <option value="ALL">Barcha holatlar</option>
            <option value="APPROVED">Tasdiqlangan</option>
            <option value="PENDING">Kutilmoqda</option>
            <option value="REJECTED">Rad etilgan</option>
          </select>

          <button
            onClick={handleOpenCreate}
            className="inline-flex items-center justify-center px-4 py-2 text-xs font-semibold text-[#091322] bg-[#c5a880] hover:bg-[#d4b993] rounded shadow-sm transition-colors cursor-pointer shrink-0"
          >
            <Plus className="w-4 h-4 mr-1.5" />
            Yangi fikr qo‘shish
          </button>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-slate-200 shadow-2xs overflow-hidden">
        <div className="divide-y divide-slate-100 text-xs">
          {loading ? (
            <div className="p-8 text-center text-slate-400">Yuklanmoqda...</div>
          ) : filteredReviews.length === 0 ? (
            <div className="p-8 text-center text-slate-400">Hech qanday sharh topilmadi</div>
          ) : (
            filteredReviews.map((r) => (
              <div key={r.id} className="p-3.5 sm:p-5 flex flex-col sm:flex-row sm:items-start justify-between gap-3 sm:gap-4 hover:bg-slate-50">
                <div className="space-y-1.5 flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-1.5 sm:space-x-2">
                    <span className="font-bold text-slate-900">{r.clientName}</span>
                    {r.clientTitle && <span className="text-slate-500 text-[11px]">({r.clientTitle})</span>}
                    <div className="flex text-[#c5a880] ml-1 sm:ml-2">
                      {[...Array(r.rating || 5)].map((_, i) => (
                        <Star key={i} className="w-3.5 h-3.5 fill-current" />
                      ))}
                    </div>
                  </div>

                  <p className="text-slate-700 italic leading-relaxed text-xs">
                    "{r.commentUz}"
                  </p>

                  <div className="flex flex-wrap items-center gap-2 sm:space-x-3 text-[11px] text-slate-400 pt-1">
                    <span>Sana: {new Date(r.createdAt).toLocaleDateString()}</span>
                    <span className={`font-semibold ${r.status === 'APPROVED' ? 'text-emerald-600' : r.status === 'REJECTED' ? 'text-rose-600' : 'text-amber-600'}`}>
                      Holat: {r.status}
                    </span>
                    {r.isFeatured && (
                      <span className="text-[#b8935a] font-semibold flex items-center">
                        <Award className="w-3 h-3 mr-1" />
                        Bosh sahifada ko‘rsatilmoqda
                      </span>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex flex-wrap items-center gap-1.5 shrink-0 pt-2 sm:pt-0 border-t sm:border-t-0 border-slate-100">
                  {r.status !== 'APPROVED' && (
                    <button
                      onClick={() => handleUpdateStatus(r.id, 'APPROVED')}
                      className="px-2.5 py-1 text-[11px] font-semibold text-emerald-700 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 rounded cursor-pointer"
                    >
                      Tasdiqlash
                    </button>
                  )}

                  {r.status !== 'REJECTED' && (
                    <button
                      onClick={() => handleUpdateStatus(r.id, 'REJECTED')}
                      className="px-2.5 py-1 text-[11px] font-semibold text-rose-700 bg-rose-50 hover:bg-rose-100 border border-rose-200 rounded"
                    >
                      Rad etish
                    </button>
                  )}

                  <button
                    onClick={() => handleToggleFeature(r.id, r.isFeatured)}
                    className={`px-2.5 py-1 text-[11px] font-semibold rounded border ${
                      r.isFeatured ? 'bg-amber-100 text-amber-800 border-amber-300' : 'bg-slate-100 text-slate-600 border-slate-300'
                    }`}
                  >
                    {r.isFeatured ? 'Belgini olish' : 'Tanlangan qilish'}
                  </button>

                  {/* Edit Review Button (Admin only) */}
                  <button
                    onClick={() => handleOpenEdit(r)}
                    className="px-2.5 py-1 text-[11px] font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200 border border-slate-300 rounded inline-flex items-center cursor-pointer"
                    title="Sharhni tahrirlash"
                  >
                    <Pencil className="w-3 h-3 mr-1 text-slate-500" />
                    Tahrirlash
                  </button>

                  <button
                    onClick={() => handleDelete(r.id)}
                    className="p-1 text-slate-400 hover:text-rose-600"
                    title="O‘chirish"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Edit Review Modal */}
      {editingReview && (
        <Modal
          isOpen={isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false);
            setEditingReview(null);
          }}
          title={`Mijoz Fikrini Tahrirlash (#${editingReview.id})`}
          maxWidth="max-w-xl"
        >
          <form onSubmit={handleSaveEdit} className="space-y-4 text-xs">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block font-semibold text-slate-700 uppercase mb-1">Mijoz Ismi *</label>
                <input
                  type="text"
                  required
                  value={editFormData.clientName}
                  onChange={(e) => setEditFormData({ ...editFormData, clientName: e.target.value })}
                  className="w-full px-3 py-2 border rounded focus:border-[#c5a880] bg-white font-medium"
                />
              </div>

              <div>
                <label className="block font-semibold text-slate-700 uppercase mb-1">Lavozim / Kompaniya</label>
                <input
                  type="text"
                  value={editFormData.clientTitle}
                  onChange={(e) => setEditFormData({ ...editFormData, clientTitle: e.target.value })}
                  placeholder="Masalan: Tadbirkor, Bosh Direktor..."
                  className="w-full px-3 py-2 border rounded focus:border-[#c5a880] bg-white"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block font-semibold text-slate-700 uppercase mb-1">Baho (Yulduzlar: 1-5)</label>
                <div className="flex items-center space-x-2 py-1.5">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      onClick={() => setEditFormData({ ...editFormData, rating: star })}
                      className="p-1 cursor-pointer transition-transform hover:scale-110"
                    >
                      <Star
                        className={`w-5 h-5 ${
                          star <= editFormData.rating
                            ? 'text-[#c59b27] fill-[#c59b27]'
                            : 'text-slate-300'
                        }`}
                      />
                    </button>
                  ))}
                  <span className="font-bold text-slate-700 ml-2 font-mono">{editFormData.rating} / 5</span>
                </div>
              </div>

              <div>
                <label className="block font-semibold text-slate-700 uppercase mb-1">Sharh Holati</label>
                <select
                  value={editFormData.status}
                  onChange={(e) => setEditFormData({ ...editFormData, status: e.target.value })}
                  className="w-full px-3 py-2 border rounded focus:border-[#c5a880] bg-white font-medium"
                >
                  <option value="APPROVED">Tasdiqlangan (APPROVED)</option>
                  <option value="PENDING">Kutilmoqda (PENDING)</option>
                  <option value="REJECTED">Rad etilgan (REJECTED)</option>
                </select>
              </div>
            </div>

            <div className="flex items-center space-x-2 py-1 bg-amber-50/70 p-3 rounded border border-amber-200">
              <input
                type="checkbox"
                id="isFeaturedReview"
                checked={editFormData.isFeatured}
                onChange={(e) => setEditFormData({ ...editFormData, isFeatured: e.target.checked })}
                className="w-4 h-4 rounded text-[#b38038] focus:ring-[#c59b27]"
              />
              <label htmlFor="isFeaturedReview" className="font-semibold text-amber-900 cursor-pointer text-xs">
                ⭐ Bosh sahifada "Mijozlar E’tirofi" qatorida ko‘rsatish (Featured)
              </label>
            </div>

            <div>
              <label className="block font-semibold text-slate-700 uppercase mb-1">
                Fikr / Izoh matni (O‘zbek tilida) *
              </label>
              <textarea
                rows={3}
                required
                value={editFormData.commentUz}
                onChange={(e) => setEditFormData({ ...editFormData, commentUz: e.target.value })}
                className="w-full px-3 py-2 border rounded focus:border-[#c5a880] bg-white leading-relaxed"
              />
            </div>

            <div>
              <label className="block font-semibold text-slate-700 uppercase mb-1">
                Fikr / Izoh matni (Rus tilida)
              </label>
              <textarea
                rows={2}
                value={editFormData.commentRu}
                onChange={(e) => setEditFormData({ ...editFormData, commentRu: e.target.value })}
                placeholder="Ruscha tarjimasi (agar kiritilmasa o‘zbekcha nusxa olinadi)..."
                className="w-full px-3 py-2 border rounded focus:border-[#c5a880] bg-white leading-relaxed"
              />
            </div>

            <div>
              <label className="block font-semibold text-slate-700 uppercase mb-1">
                Fikr / Izoh matni (Ingliz tilida)
              </label>
              <textarea
                rows={2}
                value={editFormData.commentEn}
                onChange={(e) => setEditFormData({ ...editFormData, commentEn: e.target.value })}
                placeholder="Inglizcha tarjimasi..."
                className="w-full px-3 py-2 border rounded focus:border-[#c5a880] bg-white leading-relaxed"
              />
            </div>

            <div className="pt-3 border-t border-slate-200 flex justify-end space-x-2">
              <button
                type="button"
                onClick={() => {
                  setIsEditModalOpen(false);
                  setEditingReview(null);
                }}
                className="px-4 py-2 border border-slate-300 rounded text-slate-700 hover:bg-slate-50 cursor-pointer"
              >
                Bekor qilish
              </button>
              <button
                type="submit"
                disabled={saving}
                className="px-5 py-2 bg-[#091322] text-[#c5a880] hover:bg-[#12233b] font-semibold rounded cursor-pointer disabled:opacity-50"
              >
                {saving ? 'Saqlanmoqda...' : 'Saqlash'}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* Create Review Modal */}
      <Modal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        title="Yangi Mijoz Fikrini Qo‘shish"
        maxWidth="max-w-xl"
      >
        <form onSubmit={handleSaveNew} className="space-y-4 text-xs">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block font-semibold text-slate-700 uppercase mb-1">Mijoz Ismi *</label>
              <input
                type="text"
                required
                placeholder="Masalan: Sardor Rahimberdiyev"
                value={newFormData.clientName}
                onChange={(e) => setNewFormData({ ...newFormData, clientName: e.target.value })}
                className="w-full px-3 py-2 border rounded focus:border-[#c5a880] bg-white font-medium"
              />
            </div>

            <div>
              <label className="block font-semibold text-slate-700 uppercase mb-1">Lavozim / Kompaniya</label>
              <input
                type="text"
                value={newFormData.clientTitle}
                onChange={(e) => setNewFormData({ ...newFormData, clientTitle: e.target.value })}
                placeholder="Masalan: Bosh direktor, 'Grand' MChJ"
                className="w-full px-3 py-2 border rounded focus:border-[#c5a880] bg-white"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block font-semibold text-slate-700 uppercase mb-1">Baho (Yulduzlar: 1-5)</label>
              <div className="flex items-center space-x-2 py-1.5">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    onClick={() => setNewFormData({ ...newFormData, rating: star })}
                    className="p-1 cursor-pointer transition-transform hover:scale-110"
                  >
                    <Star
                      className={`w-5 h-5 ${
                        star <= newFormData.rating
                          ? 'text-[#c59b27] fill-[#c59b27]'
                          : 'text-slate-300'
                      }`}
                    />
                  </button>
                ))}
                <span className="font-bold text-slate-700 ml-2 font-mono">{newFormData.rating} / 5</span>
              </div>
            </div>

            <div>
              <label className="block font-semibold text-slate-700 uppercase mb-1">Sharh Holati</label>
              <select
                value={newFormData.status}
                onChange={(e) => setNewFormData({ ...newFormData, status: e.target.value })}
                className="w-full px-3 py-2 border rounded focus:border-[#c5a880] bg-white font-medium"
              >
                <option value="APPROVED">Tasdiqlangan (APPROVED)</option>
                <option value="PENDING">Kutilmoqda (PENDING)</option>
                <option value="REJECTED">Rad etilgan (REJECTED)</option>
              </select>
            </div>
          </div>

          <div className="flex items-center space-x-2 py-1 bg-amber-50/70 p-3 rounded border border-amber-200">
            <input
              type="checkbox"
              id="isFeaturedNewReview"
              checked={newFormData.isFeatured}
              onChange={(e) => setNewFormData({ ...newFormData, isFeatured: e.target.checked })}
              className="w-4 h-4 rounded text-[#b38038] focus:ring-[#c59b27]"
            />
            <label htmlFor="isFeaturedNewReview" className="font-semibold text-amber-900 cursor-pointer text-xs">
              ⭐ Bosh sahifada "Mijozlar E’tirofi" blokida ko‘rsatilsin (Featured)
            </label>
          </div>

          <div>
            <label className="block font-semibold text-slate-700 uppercase mb-1">
              Fikr / Izoh matni (O‘zbek tilida) *
            </label>
            <textarea
              rows={3}
              required
              placeholder="Mijozning advokatlik xizmatlari haqidagi fikri..."
              value={newFormData.commentUz}
              onChange={(e) => setNewFormData({ ...newFormData, commentUz: e.target.value })}
              className="w-full px-3 py-2 border rounded focus:border-[#c5a880] bg-white leading-relaxed"
            />
          </div>

          <div>
            <label className="block font-semibold text-slate-700 uppercase mb-1">
              Fikr / Izoh matni (Rus tilida)
            </label>
            <textarea
              rows={2}
              value={newFormData.commentRu}
              onChange={(e) => setNewFormData({ ...newFormData, commentRu: e.target.value })}
              placeholder="Ruscha tarjimasi (bo‘sh qoldirilsa avtomatik o‘zbekcha qo‘llaniladi)..."
              className="w-full px-3 py-2 border rounded focus:border-[#c5a880] bg-white leading-relaxed"
            />
          </div>

          <div>
            <label className="block font-semibold text-slate-700 uppercase mb-1">
              Fikr / Izoh matni (Ingliz tilida)
            </label>
            <textarea
              rows={2}
              value={newFormData.commentEn}
              onChange={(e) => setNewFormData({ ...newFormData, commentEn: e.target.value })}
              placeholder="Inglizcha tarjimasi..."
              className="w-full px-3 py-2 border rounded focus:border-[#c5a880] bg-white leading-relaxed"
            />
          </div>

          <div className="pt-3 border-t border-slate-200 flex justify-end space-x-2">
            <button
              type="button"
              onClick={() => setIsCreateModalOpen(false)}
              className="px-4 py-2 border border-slate-300 rounded text-slate-700 hover:bg-slate-50 cursor-pointer"
            >
              Bekor qilish
            </button>
            <button
              type="submit"
              disabled={savingNew}
              className="px-5 py-2 bg-[#091322] text-[#c5a880] hover:bg-[#12233b] font-semibold rounded cursor-pointer disabled:opacity-50"
            >
              {savingNew ? 'Qo‘shilmoqda...' : 'Qo‘shish'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
